import { Button, Card } from "@pushpress/pushpress-ui";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { useContext } from "react";
import {
  PushPressPlanPageFormData,
  useRevenueHero,
} from "../../../hooks/useRevenueHero";
import { AppContext } from "../../../context/app";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../utils/misc";
import {
  getSubscriptionInfo,
  stripeAddOnSubscriptionInfo,
} from "../constants/subscriptionInfoMap";
import { AccountEvent } from "../constants/account.events";

interface AddOnSubscriptionCardProps {
  priceId: string;
}

const AddOnSubscriptionCard: React.FC<AddOnSubscriptionCardProps> = ({
  priceId,
}) => {
  const { client, user } = useContext(AppContext);

  const { trackEvent } = useAnalytics();
  const routerId = process.env.REVENUE_HERO_ACCOUNT_PAGE_ROUTER_ID;
  const { showScheduler } = useRevenueHero(routerId);
  const revenueHeroMetadata = { location: "Account Page", routerId };

  const handleRevenueHeroClick = async (
    formData: PushPressPlanPageFormData,
    productName: string,
  ) => {
    trackEvent(AccountEvent.RevenueHeroClick, {
      location: "Account Page",
      fullProductName: productName,
      ...formData,
      ...revenueHeroMetadata,
    });

    if (!client?.email || !client?.company) {
      trackEvent(AccountEvent.RevenueHeroFailed, {
        error: "No email or company in client context",
        ...formData,
        ...revenueHeroMetadata,
      });
      return;
    }

    try {
      await showScheduler<PushPressPlanPageFormData>({
        ...formData,
      });
    } catch (error) {
      trackEvent(AccountEvent.RevenueHeroFailed, {
        error: "Unable to book revenue hero demo - check revenue hero logs",
        ...formData,
        ...revenueHeroMetadata,
      });
      toast.error(
        "Sorry, we're experiencing issues with booking a demo. Please try again later.",
        defaultToastOptions,
      );
    }
  };

  const subscriptionInfo = getSubscriptionInfo(
    stripeAddOnSubscriptionInfo,
    priceId,
  );

  if (!subscriptionInfo) {
    trackEvent(AccountEvent.InvalidSubscriptionInfo, {
      location: "Account Page",
      subSection: "Add-On Subscription Card",
      product: priceId ?? "Unknown",
    });
    return null;
  }

  return (
    <Card className="tw-flex-1 tw-min-w-[280px] tw-max-w-[325px] tw-h-[435px]">
      <div className="tw-p-8 tw-flex tw-flex-col tw-h-full">
        <img
          className="tw-w-[245px] tw-h-[108px] tw-object-cover tw-rounded-sm"
          src={subscriptionInfo.imgSrc}
          alt="Add-On Subscription"
        />
        <h3 className="tw-text-lg tw-mt-7">{subscriptionInfo.title}</h3>
        <p className="tw-text-md tw-text-neutral-500 tw-mt-2">
          {subscriptionInfo.description}
        </p>
        <div className="tw-flex tw-flex-col tw-h-full">
          <div className="tw-flex-grow"></div>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <a
              className="tw-text-center tw-font-semibold"
              href={subscriptionInfo.marketingPageLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                trackEvent(AccountEvent.LearnMoreClicked, {
                  location: "Account Page",
                  product: subscriptionInfo.title,
                });
              }}
            >
              Learn more
            </a>
            <Button
              onClick={() =>
                handleRevenueHeroClick(
                  {
                    firstname: user.first_name ?? "",
                    lastname: user.last_name ?? "",
                    email: client?.email ?? "",
                    company: client?.company ?? "",
                    countryName: client.countryName ?? "",
                    websiteUrl: client.websiteUrl ?? "",
                    pushpressProduct: subscriptionInfo.revenueHeroProductTag,
                  },
                  subscriptionInfo.title,
                )
              }
            >
              Book a demo
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AddOnSubscriptionCard;
